/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Box, Drawer } from '@mui/material';
import type { ReactNode } from 'react';

import type { SectionTab } from '../../constants/tabs';
import { DrawerButtons } from './DrawerButtons/DrawerButtons';
import { ToggleableSection } from './ToggleableSection/ToggleableSection';
import { ToggleDrawerButton } from './ToggleDrawerButton/ToggleDrawerButton';
import type { PluginSelectionMode } from '../PluginSelection/types';

type DrawerLeftProps = {
	isSidebarOpen: boolean;
	activeTab: SectionTab;
	marginTop: string;
	onToggleSidebar: () => void;
	onTabChange: ( newValue: number ) => void;
	featuresView: PluginSelectionMode;
	isNavigationDisabled: boolean;
	children: ReactNode;
};

export function DrawerLeft( {
	isSidebarOpen,
	activeTab,
	marginTop,
	onToggleSidebar,
	onTabChange,
	featuresView,
	isNavigationDisabled,
	children
}: DrawerLeftProps ): ReactNode {
	return (
		<Drawer
			anchor={ 'left' }
			open={ isSidebarOpen }
			variant={ 'permanent' }
			transitionDuration={ 200 }
			className={ [
				'editor-customization-sidebar',
				isSidebarOpen ? 'opened' : 'closed'
			].join( ' ' ) }
			PaperProps={ {
				sx: {
					visibility: 'visible',
					transform: isSidebarOpen ?
						'translateX(0)' :
						'translateX(calc(var(--sidebar-width-closed) - var(--sidebar-width-opened)))',
					transition: theme => theme.transitions.create( 'all', {
						duration: theme.transitions.duration.shorter
					} ),
					width: 'var(--sidebar-width-opened)',
					marginTop: `calc( ${ marginTop } - 1px )`,
					height: `calc( 100vh - ${ marginTop } )`,
					boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
					display: 'flex',
					justifyContent: 'space-between'
				}
			} }
		>
			<Box
				sx={ {
					padding: 'var(--sidebar-padding-y) var(--sidebar-padding-x)',
					display: isSidebarOpen ? 'flex' : 'none',
					flexFlow: 'column',
					gap: '16px',
					overflowY: 'auto'
				} }
			>
				<ToggleableSection isVisible={ isSidebarOpen } sx={ { display: 'flex', flexFlow: 'column', minHeight: 0 } }>
					{ children }
				</ToggleableSection>
			</Box>

			<ToggleDrawerButton
				isSidebarOpen={ isSidebarOpen }
				onToggleSidebar={ onToggleSidebar }
			/>

			<ToggleableSection isVisible={ isSidebarOpen }>
				<DrawerButtons
					activeTab={ activeTab }
					onTabChange={ onTabChange }
					isNavigationDisabled={ isNavigationDisabled }
					featuresView={ featuresView }
				/>
			</ToggleableSection>
		</Drawer>
	);
}
